import { Select, type SelectOption } from "@circleco/compass/components/Select";
import type { UseAsyncSearchReturn } from "@circleco/compass/hooks/useAsyncSearch";
import { useCallback, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { isValidEmail } from "./constants";
import { useCommunityMemberHydration } from "./useCommunityMemberHydration";
import {
  type CommunityMemberOption,
  type SelectedCommunityMember,
  toSelectedCommunityMember,
  useCommunityMemberSelectOptions,
} from "./useCommunityMemberSelectOptions";

const I18N_ROOT = "community_member_select";

const isCommunityMemberOption = (
  option: SelectOption<number | string>,
): option is CommunityMemberOption => "member" in option;

interface SingleMemberSelectProps {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  includeInactive?: boolean;
  onChange?: (value: number | string | null, displayText?: string) => void;
  onSelectionComplete: () => void;
  onSelectMember?: (member: SelectedCommunityMember) => void;
  searchProps: UseAsyncSearchReturn<CommunityMemberOption>["searchProps"];
  selectProps: Record<string, unknown>;
  sharedProps: Record<string, unknown>;
  value?: number | string | null;
  valueField?: "id" | "user_id";
}

export const SingleMemberSelect = ({
  sharedProps,
  selectProps,
  searchProps,
  onChange,
  onSelectMember,
  onSelectionComplete,
  value,
  excludeAdmins = false,
  filters,
  includeInactive = false,
  valueField = "id",
}: SingleMemberSelectProps) => {
  const toast = useToast();
  const [selectionCache, setSelectionCache] =
    useState<CommunityMemberOption | null>(null);
  const [inputValue, setInputValue] = useState("");
  const hydrationIds = useMemo(() => (value != null ? [value] : []), [value]);

  const { hydratedMembers } = useCommunityMemberHydration({
    excludeAdmins,
    filters,
    ids: hydrationIds,
    includeInactive,
    valueField,
  });

  const { options: hydratedOptions } = useCommunityMemberSelectOptions({
    communityMembers: hydratedMembers,
    valueField,
  });

  const selection = useMemo(() => {
    if (value == null) return null;

    const hydrated = hydratedOptions[0];
    if (hydrated) return hydrated;

    const fromSearch = searchProps.options.find(o => o.value === value);
    if (fromSearch) return fromSearch;

    if (selectionCache?.value === value) return selectionCache;

    if (typeof value === "string" && isValidEmail(value)) {
      return { value, label: value };
    }

    return null;
  }, [value, hydratedOptions, searchProps.options, selectionCache]);

  const handleValueChange = useCallback(
    (opt: CommunityMemberOption | SelectOption<number | string> | null) => {
      setSelectionCache(opt && isCommunityMemberOption(opt) ? opt : null);
      if (opt && isCommunityMemberOption(opt)) {
        onSelectMember?.(toSelectedCommunityMember(opt));
      }
      onChange?.(opt?.value ?? null, opt?.label);
      onSelectionComplete();
    },
    [onChange, onSelectMember, onSelectionComplete],
  );

  const handleCreateOption = useCallback(
    (inputValue: string) => {
      const email = inputValue.trim();
      if (!isValidEmail(email)) {
        toast.error(t([I18N_ROOT, "invalid_email"]));
        return;
      }
      onSelectMember?.({
        id: email,
        name: email,
        email,
        avatarUrl: "",
      });
      onChange?.(email);
      onSelectionComplete();
    },
    [onChange, onSelectMember, onSelectionComplete, toast],
  );

  const handleInputValueChange: typeof searchProps.onInputValueChange =
    useCallback(
      (nextInputValue, details) => {
        setInputValue(nextInputValue);
        searchProps.onInputValueChange(nextInputValue, details);
      },
      [searchProps.onInputValueChange],
    );

  return (
    <Select
      {...sharedProps}
      {...selectProps}
      {...searchProps}
      searchable
      creatable={
        Boolean(sharedProps.creatable) && isValidEmail(inputValue.trim())
      }
      value={selection}
      onValueChange={handleValueChange}
      onCreateOption={handleCreateOption}
      onInputValueChange={handleInputValueChange}
      i18n={{ ...sharedProps }}
    />
  );
};
